import EditNote from '@mui/icons-material/EditNote';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import History from '@mui/icons-material/History';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import PictureAsPdf from '@mui/icons-material/PictureAsPdf';
import WarningAmber from '@mui/icons-material/WarningAmber';
import { Box, Checkbox, Chip, IconButton, Link, ListItemIcon, ListItemText, Menu, MenuItem, TableSortLabel, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { RPMClaimStatus } from '../../../interfaces/billing';
import { UnifiedClaim, BillingPagination } from '../../../interfaces/billing.rpm';
import { BillingService } from '../../../services';
import { StyledTheme } from '../../../styles/styleTheme';
import { getCapitalizedWord } from '../../../util/stringFormat';
import { getAperoUrl } from '../../../util/url';
import { CompactTable } from '../../common/CompactTable/CompactTable';
import { Column } from '../../common/CustomTable';
import { BillingLink } from '../common/BillingLink';
import { getRPMClaimStatusChipColor } from '../common/ChipColors';
import { ClaimLogsModal } from '../common/ClaimLogsModal';
import { CodeChipList } from '../common/CodeChipList';
import { formatInsurance } from '../common/Text';
import RPMNotePreviewModal from '../note';
import { CLAIMS } from '../constants';

// Pure function for date formatting - moved outside component for performance
const formatDateString = (dateString?: string): string => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'MM/dd/yyyy');
  } catch {
    return dateString;
  }
};

// Memoized component for action menu to prevent unnecessary re-renders
const ClaimActionsCell = memo<{
  claim: UnifiedClaim;
  onViewEdit: (id: number) => void;
  onPdf: (id: number) => void;
  onLogs: (id: number) => void;
}>(({ claim, onViewEdit, onPdf, onLogs }) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  // UnifiedClaim always has rpmNotes property
  const hasNote = claim.rpmNotes?.id;
  const hasPdf = claim.rpmNotes?.pdfUrl;

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleViewEdit = () => {
    onViewEdit(claim.id);
    handleCloseMenu();
  };

  const handlePdf = () => {
    onPdf(claim.id);
    handleCloseMenu();
  };

  const handleLogs = () => {
    onLogs(claim.id);
    handleCloseMenu();
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpenMenu}>
        <MoreHoriz />
      </IconButton>
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleViewEdit} disabled={!hasNote}>
          <ListItemIcon>
            <EditNote fontSize="small" />
          </ListItemIcon>
          <ListItemText>View/Edit Note</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePdf} disabled={!hasPdf}>
          <ListItemIcon>
            <PictureAsPdf fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleLogs}>
          <ListItemIcon>
            <History fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Logs</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
});

ClaimActionsCell.displayName = 'ClaimActionsCell';

// Exportable claim statuses (for invoice display)
const EXPORTABLE_STATUSES = [RPMClaimStatus.EXPORTED, RPMClaimStatus.EXPORTED_WITH_WARNINGS, RPMClaimStatus.NOT_EXPORTED];

interface RPMClaimsTableProps {
  data: UnifiedClaim[];
  pagination: BillingPagination;
  selectedClaims: number[];
  allRecordsSelected: boolean;
  excludedRows: { id: number; index: number }[];
  onSelectionChange: (selectedClaims: number[]) => void;
  onSelectAll: (allRecordsSelected: boolean) => void;
  onExcludeRows: (excludedRows: { id: number; index: number }[]) => void;
  onPageChange: (page: number) => void;
  onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc';
  onSort: (columnKey: string) => void;
}

export const RPMClaimsTable: React.FC<RPMClaimsTableProps> = ({
  data,
  pagination,
  selectedClaims,
  allRecordsSelected,
  excludedRows,
  onSelectionChange,
  onSelectAll,
  onExcludeRows,
  onPageChange,
  onChangeRowsPerPage,
  sortColumn,
  sortDirection,
  onSort,
}) => {
  const history = useHistory();
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [selectedClaimId, setSelectedClaimId] = useState<number | null>(null);
  const [isRpmNotePreviewModalOpen, setIsRpmNotePreviewModalOpen] = useState<boolean>(false);

  const handleSelectAll = useCallback(() => {
    if (selectedClaims.length > 0) {
      onSelectionChange([]);
    } else {
      onSelectAll(true);
    }
  }, [onSelectAll, selectedClaims, onSelectionChange]);

  const handleDeselectAll = useCallback(() => {
    onSelectAll(false);
    onExcludeRows([]);
  }, [onSelectAll, onExcludeRows]);

  const handleOpenRpmNotePreviewModal = useCallback((claimId: number | null) => {
    setSelectedClaimId(claimId);
    setIsRpmNotePreviewModalOpen(true);
  }, []);

  const handleCloseRpmNotePreviewModal = useCallback(() => {
    setSelectedClaimId(null);
    setIsRpmNotePreviewModalOpen(false);
  }, []);

  const columns: Column[] = useMemo(() => {
    return [
      {
        key: 'select',
        label: (
          <Tooltip title="Select/deselect all claims across pages" placement="right">
            <span>
              <Checkbox
                size="small"
                style={{ color: StyledTheme.tealDark }}
                checked={allRecordsSelected}
                indeterminate={selectedClaims.length > 0 && !allRecordsSelected}
                onChange={(event) =>
                  event.target.checked ? handleSelectAll() : handleDeselectAll()
                }
                aria-label="Select Claims"
              />
            </span>
          </Tooltip>
        ),
        align: 'center',
      },
      {
        key: 'id',
        label: (
          <TableSortLabel
            active={sortColumn === 'id'}
            direction={sortColumn === 'id' ? sortDirection : 'asc'}
            onClick={() => onSort('id')}
          >
            ID
          </TableSortLabel>
        ),
      },
      {
        key: 'invoiceId',
        label: 'Invoice',
      },
      {
        key: 'patientName',
        label: 'Patient Name',
      },
      {
        key: 'clinic',
        label: (
          <TableSortLabel
            active={sortColumn === 'clinic'}
            direction={sortColumn === 'clinic' ? sortDirection : 'asc'}
            onClick={() => onSort('clinic')}
          >
            Clinic
          </TableSortLabel>
        ),
      },
      {
        key: 'insurance',
        label: (
          <TableSortLabel
            active={sortColumn === 'insurance'}
            direction={sortColumn === 'insurance' ? sortDirection : 'asc'}
            onClick={() => onSort('insurance')}
          >
            Insurance
          </TableSortLabel>
        ),
      },
      {
        key: 'dateOfService',
        label: (
          <TableSortLabel
            active={sortColumn === 'dateOfService'}
            direction={sortColumn === 'dateOfService' ? sortDirection : 'asc'}
            onClick={() => onSort('dateOfService')}
          >
            DOS
          </TableSortLabel>
        ),
      },
      {
        key: 'invoiceCreationDate',
        label: (
          <TableSortLabel
            active={sortColumn === 'invoiceCreationDate'}
            direction={sortColumn === 'invoiceCreationDate' ? sortDirection : 'asc'}
            onClick={() => onSort('invoiceCreationDate')}
          >
            Created
          </TableSortLabel>
        ),
      },
      {
        key: 'processId',
        label: (
          <TableSortLabel
            active={sortColumn === 'processId'}
            direction={sortColumn === 'processId' ? sortDirection : 'asc'}
            onClick={() => onSort('processId')}
          >
            Process
          </TableSortLabel>
        ),
      },
      {
        key: 'cptCodes',
        label: 'CPT Codes',
        width: 150,
      },
      {
        key: 'status',
        label: 'Status',
      },
      {
        key: 'actions',
        label: 'Actions',
        align: 'center',
        width: 80,
      },
    ];
  }, [handleDeselectAll, handleSelectAll, allRecordsSelected, selectedClaims.length, sortColumn, sortDirection, onSort]);

  const handleOpenLogs = useCallback((claimId: number) => {
    setSelectedClaimId(claimId);
    setIsLogsModalOpen(true);
  }, []);

  const handleCloseLogsModal = () => {
    setIsLogsModalOpen(false);
    setSelectedClaimId(null);
  };

  const handleOpenPdf = useCallback(async (claimId: number | null) => {
    if (!claimId) {
      return;
    }

    const url = await new BillingService().fetchSignedUrl(claimId);
    if (url) {
      window.open(url, '_blank');
    }
  }, []);

  const handleRowSelection = useCallback(
    (id: number) => {
      // Toggle selection
      const updatedSelectedRows = selectedClaims.includes(id)
        ? selectedClaims.filter((rowId) => rowId !== id)
        : [...selectedClaims, id];

      onSelectionChange(updatedSelectedRows);

      // Update excluded rows if all records are selected
      if (allRecordsSelected) {
        const updatedExcludedRows = selectedClaims.includes(id)
          ? [...excludedRows, { id, index: id }]
          : excludedRows.filter(({ index }) => index !== id);

        onExcludeRows(updatedExcludedRows);
      }
    },
    [selectedClaims, onSelectionChange, allRecordsSelected, excludedRows, onExcludeRows],
  );

  const transformedData = useMemo(
    () =>
      data.map((claim) => {
        const errorCount = claim.errors?.length || 0;
        const warningCount = claim.warnings?.length || 0;
        const isExportableStatus = EXPORTABLE_STATUSES.includes(claim.status as RPMClaimStatus);

        return {
          id: claim.id,
          select: (
            <Checkbox
              size="small"
              checked={
                allRecordsSelected
                  ? !excludedRows.some((row) => row.id === claim.id)
                  : selectedClaims.includes(claim.id)
              }
              onChange={() => handleRowSelection(claim.id)}
              style={{ color: StyledTheme.tealDark }}
            />
          ),
          invoiceId: isExportableStatus && claim.invoiceId ? (
            <BillingLink
              url={getAperoUrl(`/invoices/${claim.invoiceId}`)}
              label={claim.invoiceId}
              defaultMessage='Invoice not available'
            />
          ) : (
            <span style={{ color: '#999' }}>Pending</span>
          ),
          patientName: `${claim.billingMember.firstName} ${claim.billingMember.lastName}`,
          clinic: claim.billingMember.clinicName,
          insurance: formatInsurance(claim.billingMember.insuranceCompany, claim.billingMember.insuranceCompanyId),
          dateOfService: formatDateString(claim.dateOfService),
          invoiceCreationDate: formatDateString(claim.createdAt), // UnifiedClaim uses createdAt
          processId: claim.processId ? (
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                history.push(`/rpms?processId=${claim.processId}`);
              }}
            >
              {claim.processId}
            </Link>
          ) : (
            <span style={{ color: '#999' }}>N/A</span>
          ),
          cptCodes: <CodeChipList codes={claim.cptCodes.map(cpt => ({ code: cpt.cptCode }))} />,
          status: (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip
                label={getCapitalizedWord(claim.status)}
                size="small"
                sx={{
                  backgroundColor: getRPMClaimStatusChipColor(claim.status).backgroundColor,
                  color: getRPMClaimStatusChipColor(claim.status).color,
                }}
              />
              {warningCount > 0 && (
                <Tooltip
                  title={
                    <Box>
                      <strong>Warnings:</strong>
                      <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                        {claim.warnings.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Chip
                    icon={<WarningAmber />}
                    label={warningCount}
                    size="small"
                    sx={{
                      backgroundColor: StyledTheme.yellowLightMui,
                      color: '#fff',
                      '& .MuiChip-icon': {
                        color: '#fff',
                        marginTop: '-2px',
                      },
                    }}
                  />
                </Tooltip>
              )}
              {errorCount > 0 && (
                <Tooltip
                  title={
                    <Box>
                      <strong>Errors:</strong>
                      <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                        {claim.errors.map((e, i) => <li key={i}>{e}</li>)}
                      </ul>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Chip
                    icon={<ErrorOutline />}
                    label={errorCount}
                    size="small"
                    sx={{
                      backgroundColor: StyledTheme.redDark,
                      color: '#fff',
                      '& .MuiChip-icon': {
                        color: '#fff',
                        marginTop: '-2px',
                      },
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          ),
          actions: (
            <ClaimActionsCell
              claim={claim}
              onViewEdit={handleOpenRpmNotePreviewModal}
              onPdf={handleOpenPdf}
              onLogs={handleOpenLogs}
            />
          ),
        };
      }),
    [
      data,
      allRecordsSelected,
      excludedRows,
      selectedClaims,
      handleRowSelection,
      handleOpenRpmNotePreviewModal,
      handleOpenPdf,
      handleOpenLogs,
      history,
    ],
  );

  const paginationProps = {
    rowsPerPageOptions: [...CLAIMS.ROWS_PER_PAGE_OPTIONS],
    rowPerPage: pagination.limit,
    page: pagination.currentPage, // Use 1-based indexing directly - CustomTable will handle conversion
    totalCount: pagination.totalCount,
    onPageChange: onPageChange, // Pass through directly - CustomTable will handle conversion
    onRowsPerPageChange: onChangeRowsPerPage,
  };

  return (
    <>
      <CompactTable
        columns={columns}
        rows={transformedData}
        pagination={paginationProps}
        defaultText="No claims found. Try adjusting your filters."
      />

      {selectedClaimId && (
        <>
          <ClaimLogsModal
            open={isLogsModalOpen}
            onClose={handleCloseLogsModal}
            claimId={selectedClaimId}
          />
          <RPMNotePreviewModal
            open={isRpmNotePreviewModalOpen}
            onClose={handleCloseRpmNotePreviewModal}
            claimId={selectedClaimId}
          />
        </>
      )}
    </>
  );
};